import { useMemo, useState } from "react";
import { currencyFormatter } from "@common/helpers/currency";
import { ScheduleTemplateMilestoneInput } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import {
    PreviewScheduleRecognitionBody,
    ScheduleRecognitionComponentInput,
    ScheduleTemplateResponse,
} from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleTermUnit } from "@common/types/domains/accounting/schedule";
import { getApiErrorMessage } from "@api/apiError";
import { usePreviewScheduleRecognition } from "@api/scheduleTemplate.api";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { formatIsoDateForDisplay } from "@helpers/date";
import {
    findAccruedPreviewError,
    ScheduleAccruedBilling,
} from "./scheduleApplyAccruedHelpers";
import { toMilestoneBodies } from "./scheduleMilestoneFormHelpers";
import { SchedulePreviewRow } from "./SchedulePreviewTable";
import {
    buildPreviewLadder,
    PreviewLadderRow,
    toAnchorsByComponentOrder,
} from "./schedulePreviewHelpers";
import { useAnsweredPreviewBody } from "./useAnsweredPreviewBody";

const PREVIEW_DEBOUNCE_MS = 400;
const PREVIEW_UNAVAILABLE_MESSAGE = "Preview is unavailable for these dates.";

export interface ScheduleApplyPreviewInput {
    template?: ScheduleTemplateResponse;
    amount: number;
    termStart: string;
    sourceDate?: string | null;
    specificDate: string;
    /** A term the schedule overrides the template's with; defaults to the template's. */
    term?: { length: number; unit: ScheduleTermUnit };
    /** Rows the schedule overrides the template's with; pass a stable array. */
    components?: ScheduleRecognitionComponentInput[];
    milestones?: ScheduleTemplateMilestoneInput[];
    /** The invoice billing the amount, checked against an accrued ladder. */
    billing?: ScheduleAccruedBilling | null;
}

export interface ScheduleApplyPreviewState {
    /** False until the form has enough for a ladder (template, amount, start). */
    isRequested: boolean;
    rows: SchedulePreviewRow[];
    /** The server's refusal, e.g. an amount too small to spread. */
    errorMessage: string | null;
    /** True once the shown preview reflects the current form values. */
    isSettled: boolean;
    /** The same ladder as rows with a description and running balance. */
    ladder: PreviewLadderRow[];
}

function buildPreviewBody({
    template,
    amount,
    termStart,
    sourceDate,
    specificDate,
    term,
    components,
    milestones,
}: ScheduleApplyPreviewInput): PreviewScheduleRecognitionBody | null {
    if (!template || amount <= 0 || !termStart) {
        return null;
    }
    if (term && term.length <= 0) {
        return null;
    }
    const previewComponents = components ?? template.components;
    return {
        amount,
        termStart,
        sourceDate: sourceDate ?? undefined,
        specificDate: specificDate || undefined,
        termLength: term?.length ?? template.termLength,
        termUnit: term?.unit ?? template.termUnit,
        components: previewComponents.map((component) => ({
            method: component.method,
            anchor: component.anchor,
            offsetDays: component.offsetDays,
            allocationPercent: component.allocationPercent,
        })),
        milestones: toMilestoneBodies({
            components: previewComponents,
            milestones: milestones ?? template.milestones,
        }),
    };
}

/**
 * The apply modal's ladder preview. Apply is gated on it: the same guard
 * that refuses a non-positive period on apply answers the preview, so the
 * form shows that message and stays disabled instead of learning it on submit.
 */
export function useScheduleApplyPreview(
    input: ScheduleApplyPreviewInput,
): ScheduleApplyPreviewState {
    const {
        template,
        amount,
        termStart,
        sourceDate,
        specificDate,
        term,
        components,
        milestones,
        billing,
    } = input;
    const termLength = term?.length;
    const termUnit = term?.unit;
    const body = useMemo(
        () =>
            buildPreviewBody({
                template,
                amount,
                termStart,
                sourceDate,
                specificDate,
                term:
                    termLength !== undefined && termUnit !== undefined
                        ? { length: termLength, unit: termUnit }
                        : undefined,
                components,
                milestones,
            }),
        [
            amount,
            components,
            milestones,
            sourceDate,
            specificDate,
            template,
            termLength,
            termStart,
            termUnit,
        ],
    );
    const [debouncedBody, setDebouncedBody] =
        useState<PreviewScheduleRecognitionBody | null>(body);

    useDebouncedEffect(
        () => {
            setDebouncedBody(body);
        },
        [body],
        PREVIEW_DEBOUNCE_MS,
    );

    const previewQuery = usePreviewScheduleRecognition(debouncedBody);
    const answeredBody = useAnsweredPreviewBody({
        requestedBody: debouncedBody,
        query: previewQuery,
    });
    const rows = useMemo<SchedulePreviewRow[]>(
        () =>
            (previewQuery.data?.rows ?? []).map((row) => ({
                id: `${row.periodKey}-${row.componentOrder}`,
                values: {
                    period: row.periodKey,
                    date: formatIsoDateForDisplay(row.effectiveDate),
                    amount: currencyFormatter.format(row.amount),
                },
            })),
        [previewQuery.data],
    );

    const ladder = useMemo<PreviewLadderRow[]>(() => {
        if (!previewQuery.data || !answeredBody) {
            return [];
        }
        return buildPreviewLadder({
            rows: previewQuery.data.rows,
            totalAmount: previewQuery.data.totalAmount,
            milestoneNames: answeredBody.milestones,
            periodLabelContext: {
                termUnit: answeredBody.termUnit,
                sourceDate: answeredBody.termStart,
                specificDate: answeredBody.specificDate ?? undefined,
                anchorsByComponentOrder: toAnchorsByComponentOrder(
                    answeredBody.components,
                ),
            },
        });
    }, [answeredBody, previewQuery.data]);

    const accruedErrorMessage =
        billing && previewQuery.data
            ? findAccruedPreviewError({
                  template,
                  billing,
                  rows: previewQuery.data.rows,
              })
            : null;

    return {
        isRequested: body !== null,
        rows,
        ladder,
        errorMessage: previewQuery.isError
            ? getApiErrorMessage(
                  previewQuery.error,
                  PREVIEW_UNAVAILABLE_MESSAGE,
              )
            : accruedErrorMessage,
        isSettled: debouncedBody === body && !previewQuery.isFetching,
    };
}
